// Heavybit Identity (Stytch) implementation of the auth facade
// (the AuthService contract in identity/auth.ts).
//
// Login is a redirect to the identity front door on this same origin:
//   login(target)  -> /identity/login?return_to=<target>
//                  -> Google or GitHub -> /identity/callback (IdentityCallback.vue
//                     exchanges the one-time token via the auth-session function
//                     and adopts the session into the Stytch browser SDK)
//                  -> router.replace(target) -> the guard finds a session.
// The facade never exchanges anything itself: it reads the ONE Stytch client
// (src/identity/stytch.ts getIdentityClient, lazily imported so the SDK loads
// only when needed) that the callback page populated. The SDK persists the
// session in cookies and re-mints the 5-minute session JWT in the background;
// getIdToken hands that JWT to Hasura and the Netlify functions.
import { unverifiedClaims } from "@auger/identity"
import { auth as config } from "@/common/config"
import { navigateTo } from "@/common/navigation"
import { scheduleCloudFrontRefresh, setCloudFrontCookies } from "@/identity/assetCookies"
import type { AuthProfile, AuthService, AuthSession } from "@/identity/auth"
import { getIdentityClient, resetIdentityClient, type StytchB2BClient } from "@/identity/stytch"
import { type IdentityPrincipal, principalFromClaims } from "@/identity/waitlist"

const enabled = !config.disabled

const LOGIN_PATH = "/identity/login"
const IDENTITY_PREFIX = "/identity/"

// ready() waits for the SDK to re-validate a cached session at boot, but no
// longer than this. Past the cap the cached JWT is used as-is: Hasura is the
// real verifier, and a session revoked meanwhile fails there as invalid-jwt,
// which forceRefreshToken turns into a login redirect. So the cap trades a
// 2-second boot stall on a slow network for one wasted request in the rare
// revoked-while-away case. Inherited from PR #1160's design.
export const READY_CAP_MS = 2000

// Force a re-mint when the current JWT is within this many ms of expiry —
// covers laptop sleep/wake gaps the SDK's background refresh has not caught
// up with yet.
const JWT_EXPIRY_SLACK_MS = 30 * 1000

// Transient failures of session.authenticate (network blips) are retried
// with this backoff before the request is surfaced as failed. Auth-ending
// failures (revoked / expired session) never retry: they redirect to login.
export const REFRESH_RETRY_DELAYS_MS = [500, 1000, 2000]

function currentAppPath(): string {
  return window.location.pathname + window.location.search + window.location.hash
}

function onIdentityRoute(): boolean {
  return window.location.pathname.startsWith(IDENTITY_PREFIX)
}

function decodeJwtPayload(jwt: string): AuthProfile | null {
  return unverifiedClaims(jwt) as AuthProfile | null
}

function jwtNearExpiry(jwt: string): boolean {
  const exp = decodeJwtPayload(jwt)?.exp
  if (typeof exp !== "number") return true
  return exp * 1000 - Date.now() < JWT_EXPIRY_SLACK_MS
}

// Stytch API errors carry status_code; 401/404 (session_not_found and
// friends) mean the session is over. Everything else is transient.
function isAuthError(e: unknown): boolean {
  const status = (e as { status_code?: unknown } | null)?.status_code
  return status === 401 || status === 404
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

// The single SDK instance, shared with the /identity/* pages.
let client: StytchB2BClient | null = null
async function sdk(): Promise<StytchB2BClient> {
  client ??= await getIdentityClient()
  return client
}

// Single-flight login redirect. navigateTo does not halt JS — the page keeps
// running while the browser navigates away — so concurrent token consumers
// (parallel Apollo queries) must share ONE redirect. The returned promise
// never resolves; callers must not proceed.
let redirecting: Promise<never> | null = null
function redirectToLogin(target?: string): Promise<never> {
  if (!redirecting) {
    const query = target ? `?return_to=${encodeURIComponent(target)}` : ""
    navigateTo(`${LOGIN_PATH}${query}`)
    redirecting = new Promise<never>(() => {})
  }
  return redirecting
}

// One-shot init: load the SDK and let it validate any cached session with
// the API (its boot-time background authenticate). Checking getSync() before
// that settles would trust a possibly-revoked cached session at the router
// guard. A signed-out client has nothing to validate and resolves at once.
//
// ready() must never reject, and must never cache a rejection. The router
// guard awaits it inside beforeEach (router/index.ts), so a throw there aborts
// navigation and leaves a blank page with no route to retry from — and the
// most likely throw is a dynamic-import failure of the SDK chunk, which is
// exactly what a stale tab hits after a deploy.
// So: log, forget the failed attempt (both here and the SDK promise inside
// identity/stytch.ts, so the next call re-imports the chunk) and resolve. The
// guard then finds no session and redirects to login, which reloads the app.
let readyPromise: Promise<void> | null = null
function ready(): Promise<void> {
  if (!enabled) return Promise.resolve()
  readyPromise ??= (async () => {
    try {
      const stytch = await sdk()
      const info = stytch.session.getInfo()
      if (!info.session || !info.fromCache) return
      await new Promise<void>((resolve) => {
        let stop: (() => void) | null = null
        const done = () => {
          stop?.()
          clearTimeout(timer)
          resolve()
        }
        const timer = setTimeout(done, READY_CAP_MS)
        stop = stytch.session.onChange(done)
      })
    } catch (e) {
      console.error("Stytch ready() failed; continuing signed out:", e)
      client = null
      resetIdentityClient()
      readyPromise = null
    }
  })()
  return readyPromise
}

function isAuthenticated(): boolean {
  if (!enabled || !client) return false
  return !!client.session.getSync()
}

// Fresh session JWT for outgoing requests. The SDK refreshes JWTs in the
// background; the near-expiry check catches sleep/wake staleness.
//
// No tokens at all means the SDK has no session: it lapsed, or it was revoked
// while the tab sat open. Redirect to login. Returning
// null instead would send `authorization: ""` to Hasura, which answers
// access-denied rather than invalid-jwt, so init/apollo.ts's recovery (which
// keys on invalid-jwt) never fires and every query in a long-lived tab fails
// with nothing on screen to explain it. redirectToLogin's promise never
// resolves, so no caller proceeds on a dead session.
async function getIdToken(): Promise<string | null> {
  if (!enabled) return null
  await ready()
  const stytch = await sdk()
  const jwt = stytch.session.getTokens()?.session_jwt ?? null
  // On an identity route the page is still establishing the session; there is
  // nothing to redirect to and doing so would bounce /identity/login off itself.
  if (!jwt) return onIdentityRoute() ? null : redirectToLogin(currentAppPath())
  if (jwtNearExpiry(jwt)) return forceRefreshToken()
  return jwt
}

async function authenticateWithBackoff(stytch: StytchB2BClient): Promise<string | null> {
  for (let attempt = 0; ; attempt++) {
    try {
      await stytch.session.authenticate()
      return stytch.session.getTokens()?.session_jwt ?? null
    } catch (e) {
      if (isAuthError(e)) return redirectToLogin(currentAppPath())
      if (attempt >= REFRESH_RETRY_DELAYS_MS.length) throw e // transient and exhausted: surface, don't log out
      console.error("Stytch session refresh failed, retrying:", e)
      await sleep(REFRESH_RETRY_DELAYS_MS[attempt]!)
    }
  }
}

// getIdToken()'s twin for the reactive recovery path: forces a real
// /sessions/authenticate round trip to re-mint the JWT. Used when Hasura
// rejects a token the client believed was still valid. Concurrent callers
// share one in-flight request.
let refreshingToken: Promise<string | null> | null = null
async function forceRefreshToken(): Promise<string | null> {
  if (!enabled) return null
  if (!refreshingToken) {
    refreshingToken = sdk()
      .then(authenticateWithBackoff)
      .finally(() => {
        refreshingToken = null
      })
  }
  return refreshingToken
}

// On an identity route the session is being established by the page itself
// (/identity/callback adopts the exchanged session); wait until the SDK
// holds it. Anywhere else there is nothing to finish.
async function handleRedirectCallback(): Promise<void> {
  if (!enabled || !onIdentityRoute()) return
  const stytch = await sdk()
  if (stytch.session.getSync()) return
  await new Promise<void>((resolve) => {
    const stop = stytch.session.onChange((session) => {
      if (!session) return
      stop()
      resolve()
    })
  })
}

// The roles from the JWT the SDK is already holding. Synchronous and
// request-free on purpose: the router guard calls it before any store loads,
// and a network member must be turned away BEFORE anything talks to Apollo.
// No session (or a token that decodes to nothing) reads as "no roles", which
// routes normally — the guard has already established that somebody is
// signed in.
function principal(): IdentityPrincipal {
  const jwt = client?.session.getTokens()?.session_jwt
  return principalFromClaims(jwt ? decodeJwtPayload(jwt) : null)
}

// x-hasura-allowed-roles from the same JWT, under the same contract as
// principal(): synchronous, request-free, and `[]` when there is nothing to
// read. A session's roles are frozen in session_custom_claims for its seven
// days, so a caller that names a role must ask the token, not assume one.
function allowedRoles(): string[] {
  const jwt = client?.session.getTokens()?.session_jwt
  const claims = (jwt ? decodeJwtPayload(jwt) : null)?.["https://hasura.io/jwt/claims"] as
    Record<string, unknown> | undefined
  const roles = claims?.["x-hasura-allowed-roles"]
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === "string") : []
}

const StytchSession: AuthService = {
  enabled,
  ready,
  isAuthenticated,
  getIdToken,
  forceRefreshToken,
  handleRedirectCallback,
  principal,
  allowedRoles,

  login(target?: string) {
    redirectToLogin(target)
  },

  // The profile is the decoded session JWT payload: it carries the
  // https://hasura.io/jwt/claims block the user store reads x-hasura-user-id
  // from. Name and email are then loaded from Postgres by currentUser.
  async resolveSession(): Promise<AuthSession> {
    await ready()
    await handleRedirectCallback()
    if (!isAuthenticated()) return { loggedIn: false, profile: null }
    const token = await getIdToken()
    if (!token) return { loggedIn: false, profile: null }
    setCloudFrontCookies(token).catch(() => {}) // fire-and-forget boot mint; scheduler owns retries
    scheduleCloudFrontRefresh(getIdToken)
    return { loggedIn: true, profile: decodeJwtPayload(token) }
  },

  // Revoke the Stytch session, then land on the sign-in page (not the app
  // root: the guard would bounce straight back into a provider redirect, and
  // Google may auto-complete it). return_to brings the next login home.
  logout() {
    sdk()
      // forceClear is not the default: without it the SDK keeps the session
      // token and member object in local storage whenever the revoke cannot
      // reach Stytch, so a sign-out during a network blip left somebody fully
      // signed in on a page that says they are signed out. See the same call
      // in views/identity/IdentityAuthorize.vue for the full reasoning.
      .then((stytch) => stytch.session.revoke({ forceClear: true }))
      .catch((e: unknown) => console.error("Stytch session revoke failed:", e))
      .finally(() => navigateTo(`${LOGIN_PATH}?return_to=${encodeURIComponent("/")}`))
  },
}

export default StytchSession
